import React, { useState, useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import actions from "../../../Store/actions";
import { strings } from "../../../common/i18n";
import AddToPhotosIcon from "@mui/icons-material/AddToPhotos";
import {
    Button,
    Dialog,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    FormControl,
    Input,
    IconButton,
    InputLabel,
    MenuItem,
    Select,
    Typography,
    DialogActions
} from "@mui/material/";
import makeStyles from "@mui/styles/makeStyles";
import styles from "./styles";
import { helpers } from "../../../common/lib";
import Loading from "../../../common/components/Loading";
import { logToSentry } from "../../../config/Sentry";

export default ({ uploadData }) => {
    const { audits, locations, auditLocations, typesList, auditControllers } =
        useSelector((state) => state);
    const dispatch = useDispatch();
    const params = useParams();
    const classes = makeStyles(styles)();

    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [fileName, setFileName] = useState("");
    const [imageType, setImageType] = useState("");
    const [selectedFile, setSelectedFile] = useState(null);
    const [fileType, setFileType] = useState(""); // "image" | "pdf" | "other"
    let isCancelled = false;

    const imageTypes = useMemo(() => {
        const { imageTypes } = typesList;
        return imageTypes.data.length > 0 ? imageTypes.data : [];
    }, [typesList]);

    // Static list for PDF/document types
    const documentTypes = useMemo(() => [{ id: "pdf", name: "Pdf" }], []);

    const currentLocation = useMemo(() => {
        if (audits.item) {
            const currentAuditLocation = auditLocations.data.find(
                (item) => item.audit === audits.item.id
            );
            const currentLocation =
                currentAuditLocation &&
                helpers.getListItemForId(
                    locations,
                    currentAuditLocation.location
                );

            return currentLocation && currentLocation.geo.length > 0
                ? currentLocation.geo[0]
                : null;
        }
        return null;
    }, [audits.item, auditLocations]);

    useEffect(() => {
        return () => {
            isCancelled = true;
        };
    }, [uploadData]);

    // ✅ Disable upload only when needed
    const disableUpload = useMemo(() => {
        if (!selectedFile) return true;
        if (fileType === "pdf") return false;
        return !imageType;
    }, [selectedFile, fileType, imageType]);

    // ✅ Handle file submission
    const handleSubmission = async () => {
        setLoading(true);
        if (Object.keys(uploadData).length !== 0 && !isCancelled) {
            try {
                const { auditAsset } =
                    uploadData.auditAssetContent[0].assetController;

                if (fileType === "pdf") {
                    const pdfData = {
                        createdDate: new Date().toISOString(),
                        documentType: 1,
                        path: selectedFile
                    };

                    uploadData.auditAssetContent[0].auditDocuments = [
                        { ...pdfData }
                    ];
                } else if (fileType === "image") {
                    const imageData = {
                        createdDate: new Date().toISOString(),
                        imageType,
                        fileType,
                        latitude: null,
                        longitude: null,
                        path: selectedFile
                    };

                    uploadData.auditAssetContent[0].auditImages = [
                        { ...imageData }
                    ];
                }

                await dispatch(
                    actions.auditControllers.create.postRequest(uploadData)
                );

                const currentAsset = auditControllers.data.filter(
                    (item) => item.auditAsset === auditAsset
                );
                if (currentAsset.length > 0) {
                    auditControllers.data.find((item) => {
                        if (item.auditAsset === auditAsset) {
                            item.auditImages = [
                                ...item.auditImages,
                                ...uploadData.auditAssetContent[0].auditImages
                            ];
                        }
                    });
                } else {
                    auditControllers.data = [
                        ...auditControllers.data,
                        uploadData.auditAssetContent[0]
                    ];
                }
                dispatch(
                    actions.toast.create.success(
                        strings("messages.evidenceUploadSuccess")
                    )
                );

                await dispatch(
                    actions.auditReview.create.getRequest(params.auditId)
                );

                // Reset fields
                setFileName("");
                setImageType("");
                setFileType("");
                setSelectedFile(null);
                setLoading(false);
            } catch (error) {
                if (!isCancelled) {
                    const errStr = error.toString();
                    console.error(errStr);
                    logToSentry("On controller upload:", { error });
                    dispatch(
                        actions.toast.create.error(
                            strings("errors.onControllerUpload")
                        )
                    );
                }
            }
        }
        onDialogClose();
    };

    const triggerFileBtnClick = () =>
        document.getElementById("imageUploadInput").click();

    const convertToBase64 = (file) => {
        return new Promise((resolve, reject) => {
            const fileReader = new FileReader();
            fileReader.readAsDataURL(file);
            fileReader.onload = () => resolve(fileReader.result);
            fileReader.onerror = (error) => reject(error);
        });
    };

    const handleFileInput = async (event) => {
        const file = event.target.files[0];
        if (!file) return;

        setFileName(file.name);
        const mimeType = file.type;

        if (mimeType.startsWith("image/")) {
            setFileType("image");
            setImageType("");
        } else if (mimeType === "application/pdf") {
            setFileType("pdf");
            setImageType("pdf"); // auto select
        } else {
            setFileType("other");
            setImageType("");
        }

        const base64 = await convertToBase64(file);
        setSelectedFile(base64);
    };

    const handleImageType = (event) => setImageType(event.target.value);

    const onDialogOpen = () => setOpen(true);
    const onDialogClose = () => {
        setOpen(false);
        setFileName("");
        setImageType("");
        setFileType("");
        setSelectedFile(null);
        setLoading(false);
    };

    if (loading) return <Loading loadingText="uploadingFile" />;

    return (
        <>
            <IconButton
                onClick={onDialogOpen}
                className={classes.auditImageAdd}
            >
                <AddToPhotosIcon size="large" color="primary" />
            </IconButton>
            <Dialog
                onClose={onDialogClose}
                aria-labelledby="imageUploadDialog"
                open={open}
            >
                <DialogTitle id="imageUploadDialogTitle">
                    {strings("messages.evidenceUploadHeader")}
                </DialogTitle>
                <Divider />
                <DialogContent>
                    <DialogContentText>
                        {strings("messages.evidenceUploadMessage")}
                    </DialogContentText>

                    {/* Hide dropdown when file is a PDF */}
                    {fileType && fileType !== "pdf" && (
                        <FormControl fullWidth margin="normal">
                            <InputLabel>
                                {fileType === "image"
                                    ? strings("imageType")
                                    : strings("documentType")}
                            </InputLabel>
                            <Select
                                label={
                                    fileType === "image"
                                        ? strings("imageType")
                                        : strings("documentType")
                                }
                                onChange={handleImageType}
                                value={imageType || ""}
                            >
                                {(fileType === "image"
                                    ? imageTypes
                                    : documentTypes
                                ).map((item) => (
                                    <MenuItem key={item.id} value={item.id}>
                                        {item.name}
                                    </MenuItem>
                                ))}
                            </Select>
                        </FormControl>
                    )}

                    <FormControl fullWidth margin="normal">
                        <Input
                            type="file"
                            name="file"
                            onChange={handleFileInput}
                            inputProps={{ accept: "image/*,application/pdf" }}
                            key={audits.id}
                            id="imageUploadInput"
                            sx={{ display: "none" }}
                        />
                    </FormControl>

                    <Button
                        variant="outlined"
                        component="span"
                        size="medium"
                        color="primary"
                        onClick={triggerFileBtnClick}
                    >
                        {strings("chooseFile")}
                    </Button>

                    {fileName && (
                        <Typography
                            component="span"
                            color="primary"
                            sx={{ mx: "5px" }}
                        >
                            {fileName}
                        </Typography>
                    )}
                </DialogContent>
                <Divider />
                <DialogActions>
                    <Button
                        onClick={handleSubmission}
                        variant="outlined"
                        color="primary"
                        disabled={disableUpload}
                    >
                        {strings("upload")}
                    </Button>
                    <Button
                        onClick={onDialogClose}
                        variant="outlined"
                        color="primary"
                    >
                        {strings("cancel")}
                    </Button>
                </DialogActions>
            </Dialog>
        </>
    );
};
